import { makeStyles, alpha } from '@material-ui/core/styles';

export const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        width: 'min(560px, 100vw)',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
    },

    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '18px 22px',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        flexShrink: 0,
    },
    headerLabels: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    headerLabel: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.07em',
        textTransform: 'uppercase',
        color: theme.palette.grey[400],
        margin: 0,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.grey[800],
        margin: 0,
    },
    closeButton: {
        padding: 4,
        color: theme.palette.grey[600],
    },

    body: {
        flex: 1,
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        padding: '16px 20px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },

    // Order summary card
    summaryCard: {
        background: '#f5f5f8',
        borderRadius: 12,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    summaryTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
    },
    summaryPlanInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        flex: 1,
    },
    summaryPlanLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.06em',
        color: theme.palette.grey[800],
        textTransform: 'uppercase',
    },
    summaryPlanDesc: {
        fontSize: 13,
        color: theme.palette.grey[600],
    },
    summaryPricePeriod: {
        fontSize: 12,
        fontWeight: 400,
        color: theme.palette.grey[600],
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
    },
    summaryPrice: {
        fontSize: 18,
        fontWeight: 700,
        color: theme.palette.grey[800],
        whiteSpace: 'nowrap',
    },
    summaryDiscount: {
        fontSize: 12,
        color: theme.palette.primary.main,
    },
    summaryDiscountAmount: {
        fontSize: 12,
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    summaryDivider: {
        borderTop: `1px solid ${theme.palette.grey[100]}`,
        margin: '4px 0',
    },
    summaryLabel: {
        fontSize: 12,
        color: theme.palette.grey[600],
    },
    summaryValue: {
        position: 'relative',
        fontSize: 12,
        lineHeight: '20px',
        color: theme.palette.grey[800],
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    summaryDueLabel: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.primary.main,
    },
    summaryDueAmount: {
        position: 'relative',
        fontSize: 15,
        lineHeight: '20px',
        fontWeight: 700,
        color: theme.palette.primary.main,
    },
    // Absolute overlay so the preview loading animation contributes no layout
    // height — the cell keeps its text line box (via a placeholder char), so
    // its baseline and height stay identical between loading and loaded states.
    summaryLoadingBox: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: 54,
    },
    // Selected add-ons line items
    addOnsSummaryList: {
        listStyleType: 'disc',
        margin: 0,
        padding: '0 0 0 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    addOnSummaryRow: {
        display: 'list-item',
    },
    addOnSummaryContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
    },
    addOnSummaryName: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: theme.palette.grey[600],
    },
    addOnSummaryPrice: {
        fontSize: 12,
        color: theme.palette.grey[800],
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    badgeRecurring: {
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        background: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
    },
    badgeOneTime: {
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.03em',
        textTransform: 'uppercase',
        background: '#eeeef1',
        color: theme.palette.grey[400],
    },

    sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.07em',
        textTransform: 'uppercase',
        color: theme.palette.grey[400],
        marginBottom: 8,
    },

    // Promo
    promoRow: {
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
    },
    promoInput: {
        flex: 1,
        '& input': {
            padding: '10px 14px',
            fontSize: 13,
            fontFamily: 'inherit',
            color: theme.palette.grey[800],
            borderRadius: 12,
            border: `1px solid ${theme.palette.grey[100]}`,
            background: '#fff',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
            '&:focus': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: -1,
                borderColor: 'transparent',
            },
        },
        '& .MuiInputBase-root': {
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': { borderColor: theme.palette.grey[100] },
            '&:hover fieldset': { borderColor: theme.palette.grey[600] },
            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 },
        },
        '& .MuiOutlinedInput-input': {
            padding: '10px 14px',
            fontSize: 13,
        },
    },
    promoApplyBtn: {
        padding: '10px 18px',
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[100]}`,
        background: '#fff',
        color: theme.palette.grey[600],
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '.04em',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
            background: '#f5f5f8',
            borderColor: '#c0c0cc',
        },
    },
    promoError: {
        fontSize: 11,
        color: '#c1272d',
        marginTop: 4,
    },
    promoSuccess: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: alpha(theme.palette.primary.main, 0.08),
        borderRadius: 10,
        padding: '10px 14px',
        marginTop: 4,
    },
    promoSuccessText: {
        fontSize: 12,
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    promoRemoveBtn: {
        fontSize: 11,
        color: theme.palette.primary.main,
        cursor: 'pointer',
        fontWeight: 600,
        background: 'none',
        border: 'none',
        padding: 0,
        '&:hover': { textDecoration: 'underline' },
    },

    // Chargify panel
    chargifyPanel: {
        borderRadius: 12,
        border: `1px solid ${theme.palette.grey[100]}`,
        background: '#fff',
        padding: '8px 12px',
        '& iframe': {
            display: 'block',
            width: '100% !important',
            height: '42px !important',
        },
    },

    // Billing address
    formGrid: {
        display: 'grid',
        gap: 8,
    },
    formRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
    },
    inputWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    inputLabel: {
        fontSize: 11,
        color: theme.palette.grey[400],
        fontWeight: 500,
    },
    inputError: {
        borderColor: '#c1272d',
        '&:focus': {
            outline: '2px solid #c1272d',
            outlineOffset: -1,
            borderColor: 'transparent',
        },
    },

    // Outlined TextField, styled to the drawer's look
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            fontSize: 13,
            backgroundColor: '#fff',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.grey[100],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.grey[100],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: '#c1272d',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: 13,
            color: '#b0b0bb',
            '&.Mui-focused': { color: theme.palette.primary.main },
            '&.Mui-error': { color: '#c1272d' },
        },
        '& .MuiFormHelperText-root': {
            fontSize: 11,
            marginLeft: 0,
            marginTop: 2,
            '&.Mui-error': { color: '#c1272d' },
        },
    },
    fieldError: {
        fontSize: 11,
        color: '#c1272d',
        marginTop: 2,
    },
    // Searchable country picker
    countrySelect: {
        position: 'relative',
    },
    countryTrigger: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        padding: '10px 14px',
        paddingRight: 36,
        fontSize: 13,
        fontFamily: 'inherit',
        color: theme.palette.grey[800],
        borderRadius: 12,
        border: `1px solid ${theme.palette.grey[100]}`,
        background: '#fff',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238a8a93' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center',
        '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -1,
            borderColor: 'transparent',
        },
    },
    countryTriggerOpen: {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -1,
        borderColor: 'transparent',
    },
    countryTriggerDisabled: {
        opacity: 0.45,
        cursor: 'default',
        pointerEvents: 'none',
    },
    countryValue: {
        color: theme.palette.grey[800],
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    countryPlaceholder: {
        color: '#b0b0bb',
    },
    countryPanel: {
        position: 'absolute',
        bottom: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        zIndex: 10,
        background: '#fff',
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(26, 26, 31, 0.14)',
        overflow: 'hidden',
    },
    countryPanelBelow: {
        bottom: 'auto',
        top: 'calc(100% + 4px)',
    },
    countrySearchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
    },
    countrySearchIcon: {
        color: theme.palette.grey[400],
        fontSize: 18,
        flexShrink: 0,
    },
    countrySearchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        fontSize: 13,
        fontFamily: 'inherit',
        color: theme.palette.grey[800],
        background: 'transparent',
        '&::placeholder': { color: '#b0b0bb' },
    },
    countryList: {
        position: 'relative',
        listStyle: 'none',
        margin: 0,
        padding: 4,
        maxHeight: 220,
        overflowY: 'auto',
    },
    countryOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        fontSize: 13,
        color: theme.palette.grey[800],
        borderRadius: 8,
        cursor: 'pointer',
    },
    countryOptionActive: {
        background: '#f5f5f8',
    },
    countryOptionSelected: {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    countryCheck: {
        color: theme.palette.primary.main,
        fontSize: 13,
        marginLeft: 8,
    },
    countryEmpty: {
        padding: '12px',
        fontSize: 13,
        color: theme.palette.grey[400],
        textAlign: 'center',
    },

    // Cardholder name sits visually below the Chargify card iframes
    cardholderInput: {
        marginTop: 8,
        display: 'block',
    },

    // Error banner
    errorBanner: {
        background: '#fff0f0',
        border: '1px solid #f5c0c0',
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 13,
        color: '#c1272d',
    },

    // Footer
    footer: {
        display: 'flex',
        gap: 8,
        padding: '14px 22px',
        borderTop: `1px solid ${theme.palette.grey[100]}`,
        flexShrink: 0,
        background: '#fff',
    },
    cancelBtn: {
        flex: '0 0 auto',
        padding: '12px 24px',
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[100]}`,
        background: '#fff',
        color: theme.palette.grey[600],
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '.04em',
        cursor: 'pointer',
        '&:hover': {
            background: '#f5f5f8',
            borderColor: '#c0c0cc',
        },
    },
    payBtn: {
        width: '100%',
        padding: '12px 24px',
        borderRadius: 4,
        border: 'none',
        background: theme.palette.primary.main,
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '.04em',
        cursor: 'pointer',
        '&:hover': {
            background: '#00006a',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },

    // Processing
    processingContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 40,
    },
    processingText: {
        fontSize: 14,
        color: theme.palette.grey[600],
        textAlign: 'center',
    },
}));

export const chargifyStyles = {
    field: {
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    input: {
        paddingTop: '2px',
        paddingBottom: '2px',
        placeholder: { color: '#b0b0bb' },
    },
    label: {
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    message: {
        paddingTop: '2px',
        paddingBottom: '2px',
    },
};
